import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import styles from './HorizontalCreativitySingleImage.module.scss';
import { setupTitleTimeline } from './HorizontalCreativitySingleImage.transitions';
import type { HorizontalCreativityProps } from './HorizontalCreativitySingleImage.data';
import Heading from '../../atoms/heading/Heading';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import useSetupTimeline from '../../../hooks/useSetupTimeline';
import useWindowSize from '../../../hooks/useWindowSize';
import gsap from 'gsap';
import Image from '../../atoms/image/Image';
import type { ImageRef } from '../../atoms/image/Image.data';
import useThemeScrollBlock from '@/hooks/useThemeScrollBlock';

const originalShapeSize = {
  w: 799,
};

export default function HorizontalCreativitySingleImage({
  blok,
  ref,
  ...props
}: HorizontalCreativityProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const contentFrameRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const title1Ref = useRef<HTMLHeadingElement>(null);
  const title2Ref = useRef<HTMLHeadingElement>(null);
  const titleOutline1Ref = useRef<HTMLHeadingElement>(null);
  const titleOutline2Ref = useRef<HTMLHeadingElement>(null);
  const shapePathRef = useRef<SVGPathElement>(null);
  const shapeRef = useRef<HTMLDivElement>(null);
  const pinWrapperRef = useRef<HTMLDivElement>(null);
  const titleWrapperRef = useRef(null);

  const imageRef = useRef<ImageRef>(null);
  const mainTimeline = useRef<gsap.core.Timeline>(undefined);
  const shapeInnerRef = useRef<HTMLDivElement>(null);
  const imageHolderRef = useRef<HTMLDivElement>(null);

  mainTimeline.current = useSetupTimeline(setupTitleTimeline, {
    title1Ref,
    title2Ref,
    titleOutline1Ref,
    titleOutline2Ref,
    shapeRef,
    imageRef,
    containerRef,
  });

  const { width, height } = useWindowSize();

  const getSize = useCallback(() => {
    const shapeBounds = shapeRef?.current?.getBoundingClientRect();
    return (shapeBounds?.width || 0) / originalShapeSize.w;
  }, [shapeRef]);

  const setSize = useCallback(
    (scale: number) => {
      gsap.set(shapePathRef.current, {
        attr: { transform: `scale(${scale})` },
      });
    },
    [shapePathRef],
  );

  useEffect(() => {
    setSize(getSize());
  }, [width, height, setSize, getSize]);

  useScrollTrigger({
    trigger: elementRef,
    pin: elementRef,
    vars: {
      scrub: true,
      start: 'top top',
      end: `100%`,
      anticipatePin: 1,
    },
  });

  useScrollTrigger({
    trigger: elementRef,
    animation: mainTimeline.current,
    vars: {
      scrub: true,
      start: 'top center',
      end: `150%`,
    },
  });

  const clipId = 'drop';

  useThemeScrollBlock(elementRef, blok.use_accent_color, {
    foreground: blok.accent_color_foreground.color,
    background: blok.accent_color_background.color,
  });

  return (
    <div
      ref={elementRef}
      className={classNames(styles.horizontalCreativity, props.className)}
      {...sbEditable(blok)}
    >
      <div ref={pinWrapperRef} className={classNames(styles.pinWrapper)}>
        <div ref={contentFrameRef} className={classNames(styles.contentFrame, 'grid-gutter')}>
          <div ref={containerRef} className={classNames(styles.container)}>
            <div className={classNames(styles.titleWrapper)}>
              <Heading
                ref={titleOutline1Ref}
                as="h2"
                size="heading-01"
                className={classNames(styles.title, styles.line1)}
                disableTriggerInOnScroll
                transitionInType="none"
              >
                {blok.line_1}
              </Heading>
              <Heading
                ref={titleOutline2Ref}
                as="h2"
                size="heading-01"
                className={classNames(styles.title, styles.line2)}
                disableTriggerInOnScroll
                transitionInType="none"
              >
                {blok.line_2}
              </Heading>
            </div>
            <div
              ref={shapeRef}
              style={{
                clipPath: `url(#${clipId})`,
                WebkitClipPath: `url(#${clipId})`,
              }}
              className={classNames(styles.shape)}
            >
              <div ref={shapeInnerRef} className={classNames(styles.shapeInner)}>
                <div ref={imageHolderRef} className={classNames(styles.imageHolder)}>
                  <Image
                    ref={imageRef}
                    src={blok.image?.filename}
                    srcDesktop={blok.image?.filename}
                    className={classNames(styles.image, 'abs-fill')}
                    layout="fill"
                    objectFit="cover"
                    alt={`${blok.line_1} ${blok.line_2}`}
                  />
                </div>

                <div className={classNames(styles.titleWrapper)} ref={titleWrapperRef}>
                  <Heading
                    ref={title1Ref}
                    as="h2"
                    size="heading-01"
                    className={classNames(styles.title, styles.line1)}
                    disableTriggerInOnScroll
                    transitionInType="none"
                  >
                    {blok.line_1}
                  </Heading>
                  <Heading
                    ref={title2Ref}
                    as="h2"
                    size="heading-01"
                    className={classNames(styles.title, styles.line2)}
                    disableTriggerInOnScroll
                    transitionInType="none"
                  >
                    {blok.line_2}
                  </Heading>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
